/**
 * Plant icon utilities — maps plant names to SVG icon filenames.
 *
 * Usage:
 *   import { getPlantIcon, getIconSrc } from '../utils/plantIconUtils';
 *   const iconName = getPlantIcon('Cherokee Purple Tomato');  // 'tomato'
 *   const src = getIconSrc('tomato');  // resolved import path
 *
 * ---------------------------------------------------------------------------
 * HOW TO ADD NEW PLANT MAPPINGS
 * ---------------------------------------------------------------------------
 *
 * There are three things you can edit, depending on what you need:
 *
 * ── 1. Map a new plant NAME to an EXISTING icon ──────────────────────────
 *   Add a line to PLANT_ICON_MAP below.
 *   Key   = lowercase plant name (how users might type it)
 *   Value = icon filename from src/assets/icons/plants/ (without .svg)
 *
 *   Example — make "edamame" use the bean icon:
 *     'edamame': 'bean',
 *
 * ── 2. Add a TYPO / ALIAS that resolves to an existing map key ───────────
 *   Add a line to PLANT_ALIASES further down.
 *   Key   = misspelling or alternate name (lowercase)
 *   Value = the correct PLANT_ICON_MAP key it should resolve to
 *
 *   Example — "soybeans" → "bean":
 *     'soybean': 'bean',
 *
 * ── 3. Add a brand-new icon file ─────────────────────────────────────────
 *   a) Drop the SVG into  src/assets/icons/plants/<name>.svg
 *      - Use lowercase-hyphenated naming (e.g. dragon-fruit.svg)
 *   b) Add the filename (without .svg) to the ALL_PLANT_ICONS array
 *      (this makes it appear in the icon picker modal)
 *   c) Add one or more entries in PLANT_ICON_MAP pointing to it
 *
 *   Example — adding a dragon fruit icon:
 *     // Step a: save file as src/assets/icons/plants/dragon-fruit.svg
 *     // Step b: add 'dragon-fruit' to ALL_PLANT_ICONS array
 *     // Step c: add to PLANT_ICON_MAP:
 *       'dragon fruit': 'dragon-fruit',
 *       'pitaya': 'dragon-fruit',
 *
 * ── Available icon files ──────────────────────────────────────────────────
 *   See the ALL_PLANT_ICONS array exported below for the full list.
 *   SVG assets live in src/assets/icons/plants/
 *
 * ── Notes ────────────────────────────────────────────────────────────────
 *   - Names are auto-normalized: lowercased, trimmed, trailing 's' removed
 *     So "Tomatoes" → "tomato" and matches automatically
 *   - Partial matching is built in: "Cherokee Purple Tomato" will match
 *     the "tomato" key even without an explicit entry
 *   - Users can always override the auto-resolved icon via the icon
 *     picker in the UI (saved to the seeds.icon column in Supabase)
 * ---------------------------------------------------------------------------
 */

// ---------------------------------------------------------------------------
// Icon map: normalized plant name key → icon filename (without extension)
// ---------------------------------------------------------------------------
export const PLANT_ICON_MAP = {
  // Vegetables — Fruiting
  'tomato': 'tomato',
  'tomatillo': 'tomatillo',
  'pepper': 'pepper-sweet',
  'bell pepper': 'pepper-sweet',
  'sweet pepper': 'pepper-sweet',
  'capsicum': 'pepper-sweet',
  'paprika': 'pepper-sweet',
  'chili': 'pepper-hot',
  'chile': 'pepper-hot',
  'hot pepper': 'pepper-hot',
  'jalapeno': 'pepper-hot',
  'jalapeño': 'pepper-hot',
  'habanero': 'pepper-hot',
  'poblano': 'pepper-hot',
  'serrano': 'pepper-hot',
  'cayenne': 'pepper-hot',
  'banana pepper': 'pepper-hot',
  'cucumber': 'cucumber',
  'cuke': 'cucumber',
  'zucchini': 'zucchini',
  'summer squash': 'zucchini',
  'patty pan': 'zucchini',
  'yellow squash': 'zucchini-yellow',
  'yellow zucchini': 'zucchini-yellow',
  'crookneck': 'zucchini-yellow',
  'cousa': 'zucchini',
  'chayote': 'zucchini',
  'eight ball squash': 'zucchini',
  'acorn squash': 'acorn-squash',
  'kabocha': 'acorn-squash',
  'buttercup squash': 'acorn-squash',
  'triamble': 'acorn-squash',
  'yokohama': 'acorn-squash',
  'butternut squash': 'butternut-squash',
  'butternut': 'butternut-squash',
  'delicata': 'butternut-squash',
  'hubbard': 'butternut-squash',
  'pumpkin': 'pumpkin',
  'winter squash': 'gourd-1',
  'spaghetti squash': 'gourd-1',
  'gourd': 'pumpkin',
  'eggplant': 'eggplant',
  'aubergine': 'eggplant',
  'brinjal': 'eggplant',
  'corn': 'corn',
  'sweet corn': 'corn',
  'popcorn': 'corn',
  'maize': 'corn',
  'sweetcorn': 'corn',
  'bean': 'bean',
  'green bean': 'bean',
  'wax bean': 'bean',
  'pole bean': 'bean',
  'bush bean': 'bean',
  'snap bean': 'bean',
  'runner bean': 'bean',

  // Vegetables — Roots & Bulbs
  'carrot': 'carrot',
  'parsnip': 'parsnip',
  'radish': 'radish',
  'daikon': 'radish',
  'mooli': 'radish',
  'beet': 'beetroot',
  'beetroot': 'beetroot',
  'turnip': 'turnip',
  'kohlrabi': 'turnip',
  'rutabaga': 'turnip',
  'swede': 'turnip',
  'neep': 'turnip',
  'bunching onion': 'onion-green',
  'evergreen bunching': 'onion-green',
  'green onion': 'onion-green',
  'scallion': 'onion-green',
  'spring onion': 'onion-green',
  'leek': 'onion-green',
  'onion': 'onion',
  'shallot': 'onion',
  'garlic': 'garlic',
  'sweet potato': 'yam',
  'yam': 'yam',
  'kumara': 'yam',
  'batata': 'yam',
  'potato': 'potato',
  'spud': 'potato',
  'ginger': 'ginger',
  'turmeric': 'ginger',

  // Vegetables — Leafy Greens
  'lettuce': 'lettuce',
  'romaine': 'lettuce',
  'butterhead': 'lettuce',
  'looseleaf': 'lettuce',
  'iceberg': 'lettuce',
  'oakleaf': 'lettuce',
  'crisphead': 'lettuce',
  'cos': 'lettuce',
  'kale': 'kale',
  'collard': 'kale',
  'collard green': 'kale',
  'mustard green': 'kale',
  'cavolo nero': 'kale',
  'chard': 'chard',
  'swiss chard': 'chard',
  'rainbow chard': 'chard',
  'silverbeet': 'chard',
  'rhubarb': 'chard',
  'spinach': 'spinach',
  'arugula': 'spinach',
  'rocket': 'spinach',
  'sorrel': 'spinach',
  'mache': 'spinach',
  'watercress': 'spinach',
  'endive': 'spinach',
  'radicchio': 'spinach',
  'cabbage': 'cabbage',
  'bok choy': 'cabbage',
  'pak choi': 'cabbage',
  'napa cabbage': 'cabbage',
  'savoy cabbage': 'cabbage',
  'wombok': 'cabbage',
  'celery': 'celery',
  'celeriac': 'celery',
  'fennel': 'celery',

  // Vegetables — Brassicas & Others
  'broccoli': 'broccoli',
  'broccolini': 'broccoli',
  'romanesco': 'broccoli',
  'broccoli raab': 'broccoli',
  'rapini': 'broccoli',
  'calabrese': 'broccoli',
  'cauliflower': 'cauliflower',
  'brussels sprout': 'brussels-sprouts',
  'brussels sprouts': 'brussels-sprouts',
  'brussel sprout': 'brussels-sprouts',
  'pea': 'pea',
  'snow pea': 'pea',
  'snap pea': 'pea',
  'sugar snap pea': 'pea',
  'edamame': 'pea',
  'mangetout': 'pea',
  'asparagus': 'asparagus',
  'artichoke': 'artichoke',
  'cardoon': 'artichoke',

  // Herbs
  'cilantro': 'cilantro',
  'coriander': 'cilantro',
  'parsley': 'cilantro',
  'chervil': 'cilantro',
  'mint': 'cilantro',
  'dill': 'dill',
  'dillweed': 'dill',
  'dill weed': 'dill',
  'chive': 'chives',
  'chives': 'chives',
  'garlic chive': 'chives',
  'lemongrass': 'lemongrass',
  'lemon grass': 'lemongrass',
  'rosemary': 'herb-med',
  'lavender': 'herb-med',
  'herb': 'herb',
  'basil': 'herb',
  'oregano': 'herb',
  'thyme': 'herb',
  'sage': 'herb',
  'tarragon': 'herb',
  'marjoram': 'herb',
  'savory': 'herb',
  'lovage': 'herb',
  'stevia': 'herb',
  'shiso': 'herb',
  'lemon balm': 'herb',

  // Flowers
  'sunflower': 'sunflower',
  'calendula': 'calendula',
  'pot marigold': 'calendula',
  'marigold': 'marigold',
  'nasturtium': 'nasturtium',
  'zinnia': 'zinnia',
  'daisy': 'daisy',
  'shasta daisy': 'daisy',
  'chamomile': 'daisy',
  'camomile': 'daisy',
  'poppy': 'poppy',
  "bachelor's button": 'bachelors-button',
  'bachelors button': 'bachelors-button',
  'cornflower': 'bachelors-button',
  'centaurea': 'bachelors-button',
  'hydrangea': 'hydrangea',
  'hortensia': 'hydrangea',
  'dahlia': 'dahlia',

  // Berries & Small Fruit
  'strawberry': 'strawberry',
  'blueberry': 'blueberry',
  'huckleberry': 'blueberry',
  'bilberry': 'blueberry',
  'honeyberry': 'blueberry',
  'serviceberry': 'blueberry',
  'juneberry': 'blueberry',
  'raspberry': 'raspberry',
  'blackberry': 'raspberry',
  'boysenberry': 'raspberry',
  'loganberry': 'raspberry',
  'marionberry': 'raspberry',
  'dewberry': 'raspberry',
  'bramble': 'raspberry',
  'grape': 'grape',
  'watermelon': 'watermelon',
  'cantaloupe': 'melon',
  'honeydew': 'melon',
  'muskmelon': 'melon',
  'rockmelon': 'melon',
  'melon': 'melon',

  // Tree Fruits
  'apple': 'apple',
  'crabapple': 'apple',
  'pear': 'pear',
  'quince': 'pear',
  'asian pear': 'pear',
  'nashi': 'pear',
  'peach': 'peach',
  'apricot': 'apricot',
  'nectarine': 'apricot',
  'plum': 'plum',
  'prune': 'plum',
  'damson': 'plum',
  'greengage': 'plum',
  'sloe': 'plum',
  'cherry': 'cherry',
  'morello': 'cherry',
  'fig': 'fig',
  'mulberry': 'fig',
  'persimmon': 'fig',
  'pawpaw': 'fig',
  'loquat': 'fig',
  'pomegranate': 'pomegranate',
  'lemon': 'lemon',
  'lime': 'lime',
  'orange': 'orange',
  'mandarin': 'orange',
  'clementine': 'orange',
  'tangerine': 'orange',
  'grapefruit': 'orange',
  'blood orange': 'orange',
  'citrus': 'orange',
  'satsuma': 'orange',
  'tangelo': 'orange',

  // Perennials & Other
  'cover crop': 'cover-crop',
  'clover': 'cover-crop',
  'buckwheat': 'cover-crop',
  'rye': 'cover-crop',
  'oats': 'cover-crop',
  'vetch': 'cover-crop',
  'green manure': 'cover-crop',
  'mushroom': 'mushroom',
  'fungi': 'mushroom',
  'mycelium': 'mushroom',
};

// ---------------------------------------------------------------------------
// Alias map: misspellings & regional alternate names → normalized key
// ---------------------------------------------------------------------------
export const PLANT_ALIASES = {
  // Tomato
  'tomatoe': 'tomato',
  'tamato': 'tomato',
  'tommato': 'tomato',
  // Tomatillo
  'tomatilo': 'tomatillo',
  'tomatillio': 'tomatillo',
  // Pepper
  'peper': 'pepper',
  'papper': 'pepper',
  // Chili
  'chilly': 'chili',
  'chily': 'chili',
  // Cucumber
  'cucmber': 'cucumber',
  'cucuber': 'cucumber',
  // Zucchini
  'zuccini': 'zucchini',
  'zuchinni': 'zucchini',
  'zuchinhi': 'zucchini',
  'courgett': 'zucchini',
  'courgette': 'zucchini',
  'baby marrow': 'zucchini',
  'yellow courgette': 'yellow zucchini',
  // Pumpkin
  'pumkin': 'pumpkin',
  'pumpkn': 'pumpkin',
  'marrow': 'pumpkin',
  // Eggplant
  'eggplannt': 'eggplant',
  'aubergiene': 'aubergine',
  'brinjall': 'brinjal',
  // Corn
  'cron': 'corn',
  // Carrot
  'carot': 'carrot',
  'carott': 'carrot',
  'caret': 'carrot',
  // Parsnip
  'parnsip': 'parsnip',
  'parsnipp': 'parsnip',
  // Radish
  'raddish': 'radish',
  'radich': 'radish',
  'radis': 'radish',
  // Beet
  'beet root': 'beetroot',
  'beatroot': 'beetroot',
  'beettroot': 'beetroot',
  // Turnip
  'turnipp': 'turnip',
  'rutabega': 'rutabaga',
  // Onion
  'onoin': 'onion',
  'oinon': 'onion',
  'shalot': 'shallot',
  'shallott': 'shallot',
  'scallian': 'scallion',
  'greem onion': 'green onion',
  'sprng onion': 'spring onion',
  // Garlic
  'garlik': 'garlic',
  'garlc': 'garlic',
  // Potato
  'potatoe': 'potato',
  'potatos': 'potato',
  'pottato': 'potato',
  // Sweet potato
  'sweet potatoe': 'sweet potato',
  'swet potato': 'sweet potato',
  // Ginger
  'gingr': 'ginger',
  'tumeric': 'turmeric',
  'turmuric': 'turmeric',
  // Lettuce
  'letuce': 'lettuce',
  'letttuce': 'lettuce',
  'lettus': 'lettuce',
  'romiane': 'romaine',
  // Kale
  'kail': 'kale',
  'coallard': 'collard',
  // Chard
  'chrd': 'chard',
  'swis chard': 'swiss chard',
  'rubarb': 'rhubarb',
  // Spinach
  'spinich': 'spinach',
  'arugala': 'arugula',
  'roquette': 'rocket',
  // Cabbage
  'bok choi': 'bok choy',
  // Celery
  'celerey': 'celery',
  'fennal': 'fennel',
  // Broccoli
  'brocoli': 'broccoli',
  'brocolli': 'broccoli',
  // Cauliflower
  'califlower': 'cauliflower',
  'caluiflower': 'cauliflower',
  // Brussels sprouts
  'brussle sprouts': 'brussels sprouts',
  'brusels sprouts': 'brussels sprouts',
  // Pea
  'pees': 'pea',
  'snow pee': 'snow pea',
  'snapea': 'snap pea',
  // Asparagus
  'asparagas': 'asparagus',
  'aspargus': 'asparagus',
  'asperagus': 'asparagus',
  // Artichoke
  'artichoak': 'artichoke',
  'artchoke': 'artichoke',
  // Herbs
  'oregeno': 'oregano',
  'majoran': 'marjoram',
  'basill': 'basil',
  'baisl': 'basil',
  'mintt': 'mint',
  'shizo': 'shiso',
  'corainder': 'coriander',
  'parsely': 'parsley',
  'lemmongrass': 'lemongrass',
  'lemon-grass': 'lemongrass',
  'lavendar': 'lavender',
  'chivs': 'chive',
  // Flowers
  'sunflour': 'sunflower',
  'sunflwer': 'sunflower',
  'marigolde': 'marigold',
  'calandula': 'calendula',
  'nasturtiam': 'nasturtium',
  'nasturtion': 'nasturtium',
  'zinia': 'zinnia',
  'zinea': 'zinnia',
  'daisey': 'daisy',
  'daisie': 'daisy',
  'chamomille': 'chamomile',
  'popy': 'poppy',
  'poppi': 'poppy',
  'bachellors button': "bachelor's button",
  'cornflour': 'cornflower',
  'hydrangia': 'hydrangea',
  'hydrangeia': 'hydrangea',
  // Berries
  'strawbery': 'strawberry',
  'stawberry': 'strawberry',
  'strawberrry': 'strawberry',
  'bluebery': 'blueberry',
  'rasberry': 'raspberry',
  'raspbery': 'raspberry',
  'blackbery': 'blackberry',
  'grpe': 'grape',
  'watermellon': 'watermelon',
  'water melon': 'watermelon',
  'cantalope': 'cantaloupe',
  'cantelope': 'cantaloupe',
  // Tree fruits
  'aple': 'apple',
  'appple': 'apple',
  'pere': 'pear',
  'qince': 'quince',
  'peech': 'peach',
  'apricott': 'apricot',
  'aprecot': 'apricot',
  'plumb': 'plum',
  'damsen': 'damson',
  'chery': 'cherry',
  'cheery': 'cherry',
  'figg': 'fig',
  'mulbery': 'mulberry',
  'pomegranite': 'pomegranate',
  'pomegrante': 'pomegranate',
  'lim': 'lime',
  'ornge': 'orange',
  'mandarine': 'mandarin',
  // Other
  'cover crp': 'cover crop',
  'musroom': 'mushroom',
  'mushrom': 'mushroom',
  'fungas': 'fungi',
};

// ---------------------------------------------------------------------------
// All available icon filenames (for the icon picker)
// ---------------------------------------------------------------------------
export const ALL_PLANT_ICONS = [
  // Vegetables — Fruiting
  'tomato', 'tomatillo', 'pepper-sweet', 'pepper-hot', 'cucumber', 'zucchini',
  'zucchini-yellow', 'acorn-squash', 'butternut-squash', 'pumpkin', 'gourd-1',
  'eggplant', 'corn', 'bean',
  // Vegetables — Roots & Bulbs
  'carrot', 'parsnip', 'parsnip-2', 'radish', 'beetroot', 'turnip', 'onion', 'onion-green',
  'garlic', 'potato', 'yam', 'ginger',
  // Vegetables — Leafy Greens
  'lettuce', 'lettuce-2', 'lettuce-3', 'kale', 'chard', 'spinach', 'cabbage', 'celery',
  // Vegetables — Brassicas & Others
  'broccoli', 'cauliflower', 'brussels-sprouts', 'pea', 'asparagus', 'artichoke',
  // Herbs
  'herb', 'herb-med', 'cilantro', 'dill', 'chives', 'lemongrass',
  // Flowers
  'sunflower', 'marigold', 'calendula', 'nasturtium', 'zinnia', 'daisy', 'poppy',
  'bachelors-button', 'hydrangea', 'dahlia',
  'flower-1', 'flower-2', 'flower-4', 'flower-5', 'flower-6', 'flower-7',
  // Berries & Small Fruit
  'strawberry', 'blueberry', 'raspberry', 'grape', 'melon', 'watermelon',
  // Tree Fruits
  'apple', 'pear', 'peach', 'apricot', 'plum', 'cherry', 'fig', 'pomegranate',
  'lemon', 'lime', 'orange',
  // Other
  'cover-crop', 'mushroom',
  // Seedlings & Trees (generic)
  'seedling-default', 'seedling-1', 'seedling-2', 'tree',
];

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

/**
 * Normalize a plant name for lookup:
 * - lowercases
 * - trims
 * - collapses multiple spaces
 * - removes trailing 's' for basic depluralization
 */
export const normalizePlantName = (name) => {
  if (!name || typeof name !== 'string') return '';
  let n = name.toLowerCase().trim().replace(/\s+/g, ' ');
  // Basic depluralization: remove trailing 's' but keep 'ss' (e.g. grass)
  if (n.length > 2 && n.endsWith('s') && !n.endsWith('ss')) {
    n = n.slice(0, -1);
  }
  return n;
};

// ---------------------------------------------------------------------------
// Main lookup function
// ---------------------------------------------------------------------------

// Pre-sorted MAP keys (longest-first) for partial matching — cached once at
// module scope so _resolveIcon doesn't re-sort on every call.
const _sortedMapKeys = Object.keys(PLANT_ICON_MAP).sort((a, b) => b.length - a.length);

/**
 * Internal resolution — tries exact match, alias match, and partial match.
 * Returns the icon filename or null if no match found.
 */
const _resolveIcon = (normalized) => {
  if (!normalized) return null;

  // 1. Exact match
  if (PLANT_ICON_MAP[normalized]) return PLANT_ICON_MAP[normalized];

  // 2. Alias match — resolve alias to a key, then check if that key (or its
  //    resolution) is in the icon map
  if (PLANT_ALIASES[normalized]) {
    const aliasTarget = PLANT_ALIASES[normalized];
    if (PLANT_ICON_MAP[aliasTarget]) return PLANT_ICON_MAP[aliasTarget];
    const normalizedAlias = normalizePlantName(aliasTarget);
    if (PLANT_ICON_MAP[normalizedAlias]) return PLANT_ICON_MAP[normalizedAlias];
  }

  // 3. Check aliases with partial match (misspelling might be a substring)
  for (const [alias, target] of Object.entries(PLANT_ALIASES)) {
    if (normalized.includes(alias)) {
      const resolved = PLANT_ICON_MAP[target] || PLANT_ICON_MAP[normalizePlantName(target)];
      if (resolved) return resolved;
    }
  }

  // 4. Partial match — check if normalized name contains any MAP key
  for (const key of _sortedMapKeys) {
    if (normalized.includes(key)) return PLANT_ICON_MAP[key];
  }

  return null;
};

/**
 * Get the icon filename for a given plant name.
 *
 * Resolution order:
 * 1. If userOverride is provided and non-null, return it
 * 2. If variety is provided, try combined lookups:
 *    "variety plant" then "plant variety" (e.g. "Yellow Zucchini", "Zucchini Yellow")
 * 3. Standard resolution on plant name alone (exact, alias, partial)
 * 4. Fallback to 'seedling-default'
 *
 * @param {string} plantName - The plant name to look up
 * @param {string|null} [userOverride] - User's manually chosen icon (takes precedence)
 * @param {string|null} [variety] - Optional variety to combine with plant name for better matching
 * @returns {string} Icon filename (without extension)
 */
export const getPlantIcon = (plantName, userOverride, variety) => {
  if (userOverride) return userOverride;

  const normalized = normalizePlantName(plantName);
  if (!normalized) return 'seedling-default';

  // If variety is provided, try combined lookups first
  if (variety) {
    const varNorm = normalizePlantName(variety);
    if (varNorm) {
      const result = _resolveIcon(`${varNorm} ${normalized}`) || _resolveIcon(`${normalized} ${varNorm}`);
      if (result) return result;
    }
  }

  // Standard resolution on plant name only
  return _resolveIcon(normalized) || 'seedling-default';
};

// ---------------------------------------------------------------------------
// Icon source helper — use dynamic require for SVG imports
// ---------------------------------------------------------------------------

// Cache for loaded icon modules
const iconCache = {};

/**
 * Get the image source (URL) for an icon filename.
 * Uses require() to resolve the path through webpack.
 *
 * @param {string} iconName - Icon filename without extension (e.g. 'tomato')
 * @returns {string} The resolved path/URL for the SVG file
 */
export const getIconSrc = (iconName) => {
  if (!iconName) return getIconSrc('seedling-default');

  if (iconCache[iconName]) return iconCache[iconName];

  try {
    // eslint-disable-next-line no-undef
    const src = require(`../assets/icons/plants/${iconName}.svg`);
    iconCache[iconName] = src;
    return src;
  } catch {
    // If the icon file doesn't exist, fall back to seedling-default
    if (iconName !== 'seedling-default') {
      return getIconSrc('seedling-default');
    }
    return '';
  }
};
